import type { BoardObject } from './types'

/**
 * Marquee selection geometry for the board canvas.
 *
 * Kept out of `pages/board-canvas.tsx` because it is the half of drag-select
 * that can be wrong in ways a screenshot never shows — a rule that reads fine
 * and picks the wrong objects — and because it is pure, so it can be tested
 * without a browser. See `scripts/board-select-check.mjs`.
 *
 * All coordinates are BOARD space, never screen space: the canvas is panned and
 * zoomed, and a rectangle in screen pixels would select different objects at
 * different zoom levels.
 */

export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

type Box = Pick<BoardObject, 'x' | 'y' | 'width' | 'height'>

/**
 * The rectangle between two drag points, whichever way the drag went.
 *
 * Dragging up-and-left is as natural as down-and-right, and produces negative
 * width/height if taken literally — which silently matches nothing.
 */
export function normalizeRect(a: { x: number; y: number }, b: { x: number; y: number }): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  }
}

/** Do the two boxes overlap at all? Touching edges count as overlapping. */
export function intersects(r: Rect, o: Box): boolean {
  return (
    r.x <= o.x + o.width &&
    o.x <= r.x + r.width &&
    r.y <= o.y + o.height &&
    o.y <= r.y + r.height
  )
}

/** Is the box entirely inside the rectangle? */
export function contains(r: Rect, o: Box): boolean {
  return (
    o.x >= r.x &&
    o.y >= r.y &&
    o.x + o.width <= r.x + r.width &&
    o.y + o.height <= r.y + r.height
  )
}

/**
 * The ids a marquee should select, in paint order.
 *
 * Two exceptions to "anything the rectangle touches", both learned from what
 * the obvious rule does on a real board:
 *
 * - **A frame must be fully enclosed.** Frames are backdrops that usually
 *   contain the very shapes you are lassoing, so an intersection test hands you
 *   the frame every time you rubber-band two notes inside it — and then the
 *   group drag moves the frame and everything else sitting on it. Requiring
 *   containment means you select a frame by lassoing *around* it, which is also
 *   how you'd describe the intent out loud.
 *
 * - **A connected arrow is never selected.** Its geometry is recomputed from
 *   the two shapes it joins on every render, so translating it does nothing
 *   visible, and committing that translation writes a position no one reads.
 *   It follows its endpoints instead. Free-floating arrows carry their own
 *   geometry and are selected like any other shape.
 */
export function objectsInMarquee(objects: BoardObject[], rect: Rect): string[] {
  return objects
    .filter((o) => {
      if (o.kind === 'arrow' && o.from_object_id && o.to_object_id) return false
      return o.kind === 'frame' ? contains(rect, o) : intersects(rect, o)
    })
    .map((o) => o.id)
}

/** Smallest rectangle covering every box. Null when the list is empty. */
export function boundsOf(boxes: Box[]): Rect | null {
  if (boxes.length === 0) return null
  const minX = Math.min(...boxes.map((b) => b.x))
  const minY = Math.min(...boxes.map((b) => b.y))
  const maxX = Math.max(...boxes.map((b) => b.x + b.width))
  const maxY = Math.max(...boxes.map((b) => b.y + b.height))
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}
